import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Truck, User, Phone, Mail, Car, FileText, Camera, 
  Upload, CheckCircle, ArrowLeft, ArrowRight, Eye, EyeOff 
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';

const OperatorApplication = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    // Personal Info
    name: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: '',
    // Vehicle Info
    vehicle_type: 'pickup_truck',
    vehicle_make: '',
    vehicle_model: '',
    vehicle_year: '',
    plate_number: '',
    load_capacity_kg: '',
    // Documents (URLs - in production would upload files)
    drivers_license_url: '',
    vehicle_registration_url: '',
    insurance_document_url: '',
    load_capacity_proof_url: '',
    vehicle_photos: []
  });

  const vehicleTypes = [
    { id: 'pickup_truck', name: 'Pickup Truck', icon: '🛻', desc: 'Small loads up to 1 ton' },
    { id: 'mini_truck', name: 'Mini Truck', icon: '🚚', desc: 'Medium loads, 1-3 tons' },
    { id: 'large_truck', name: 'Large Truck', icon: '🚛', desc: 'Heavy loads, 5+ tons' },
    { id: 'cargo_van', name: 'Cargo Van', icon: '🚐', desc: 'Parcels and small items' }
  ];

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.name || !formData.phone || !formData.password) {
      toast({
        title: 'Missing Fields',
        description: 'Please fill in all required personal information',
        variant: 'destructive'
      });
      setStep(1);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast({
        title: 'Password Mismatch',
        description: 'Passwords do not match',
        variant: 'destructive'
      });
      setStep(1);
      return;
    }

    if (!formData.vehicle_make || !formData.plate_number || !formData.load_capacity_kg) {
      toast({
        title: 'Missing Vehicle Info',
        description: 'Please fill in all vehicle details',
        variant: 'destructive'
      });
      setStep(2);
      return;
    }

    setLoading(true);
    try {
      // First register the user
      const registerRes = await api.post('/auth/register', {
        name: formData.name,
        phone: formData.phone,
        email: formData.email || undefined,
        password: formData.password,
        role: 'operator'
      });

      if (registerRes.data.token) {
        localStorage.setItem('token', registerRes.data.token);
        localStorage.setItem('user', JSON.stringify(registerRes.data.user));
      }

      // Then submit operator application
      await api.post('/operators/apply', {
        name: formData.name,
        phone: formData.phone,
        email: formData.email || null,
        vehicle_type: formData.vehicle_type,
        vehicle_make: formData.vehicle_make,
        vehicle_model: formData.vehicle_model,
        vehicle_year: parseInt(formData.vehicle_year) || new Date().getFullYear(),
        plate_number: formData.plate_number,
        load_capacity_kg: parseFloat(formData.load_capacity_kg),
        drivers_license_url: formData.drivers_license_url || 'pending_upload',
        vehicle_registration_url: formData.vehicle_registration_url || 'pending_upload',
        insurance_document_url: formData.insurance_document_url || 'pending_upload',
        load_capacity_proof_url: formData.load_capacity_proof_url || 'pending_upload',
        vehicle_photos: formData.vehicle_photos.length > 0 ? formData.vehicle_photos : ['pending_upload']
      });

      toast({
        title: 'Application Submitted!',
        description: 'We will review your application and notify you once approved.'
      });

      navigate('/operator/pending');
    } catch (error) {
      toast({
        title: 'Submission Failed',
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white p-6">
        <div className="max-w-2xl mx-auto">
          <button 
            onClick={() => navigate('/operator/login')}
            className="flex items-center gap-2 text-orange-100 hover:text-white mb-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Login
          </button>
          <div className="flex items-center gap-3">
            <Truck className="h-8 w-8" />
            <div>
              <h1 className="text-2xl font-bold">Become a Cargo Operator</h1>
              <p className="text-orange-100">Join Tekstar Logistics and start earning</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4">
        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-6 mt-4">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div 
                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                  step >= s 
                    ? 'bg-orange-500 text-white' 
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {step > s ? <CheckCircle className="h-5 w-5" /> : s}
              </div>
              {s < 3 && <div className={`w-16 h-1 ${step > s ? 'bg-orange-500' : 'bg-gray-200'}`} />}
            </div>
          ))}
        </div>
        <div className="flex justify-center gap-12 text-sm text-gray-600 mb-6">
          <span className={step >= 1 ? 'text-orange-600 font-medium' : ''}>Personal</span>
          <span className={step >= 2 ? 'text-orange-600 font-medium' : ''}>Vehicle</span>
          <span className={step >= 3 ? 'text-orange-600 font-medium' : ''}>Documents</span>
        </div>

        {/* Step 1: Personal Information */}
        {step === 1 && (
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <User className="h-5 w-5 text-orange-500" />
              Personal Information
            </h2>

            <div className="space-y-4">
              <div>
                <Label>Full Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter your full name"
                  data-testid="operator-name"
                />
              </div>

              <div>
                <Label>Phone Number *</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+260..."
                    className="pl-10"
                    data-testid="operator-phone-input"
                  />
                </div>
              </div>

              <div>
                <Label>Email (Optional)</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your@email.com"
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Password *</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="Create password"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label>Confirm Password *</Label>
                  <div className="relative">
                    <Input
                      type={showConfirmPassword ? "text" : "password"}
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      placeholder="Confirm password"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>

              <Button 
                onClick={() => setStep(2)}
                disabled={!formData.name || !formData.phone || !formData.password}
                className="w-full bg-orange-500 hover:bg-orange-600"
              >
                Continue
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </Card>
        )}

        {/* Step 2: Vehicle Information */}
        {step === 2 && (
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Car className="h-5 w-5 text-orange-500" />
              Vehicle Information
            </h2>

            <div className="space-y-4">
              <div>
                <Label>Vehicle Type *</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {vehicleTypes.map((vt) => (
                    <button
                      key={vt.id}
                      onClick={() => setFormData({ ...formData, vehicle_type: vt.id })}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        formData.vehicle_type === vt.id
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-gray-200 hover:border-orange-200'
                      }`}
                      data-testid={`vehicle-type-${vt.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{vt.icon}</span>
                        <span className="font-medium">{vt.name}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{vt.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Vehicle Make *</Label>
                  <Input
                    value={formData.vehicle_make}
                    onChange={(e) => setFormData({ ...formData, vehicle_make: e.target.value })}
                    placeholder="e.g., Toyota"
                  />
                </div>
                <div>
                  <Label>Vehicle Model *</Label>
                  <Input
                    value={formData.vehicle_model}
                    onChange={(e) => setFormData({ ...formData, vehicle_model: e.target.value })}
                    placeholder="e.g., Hilux"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Year</Label>
                  <Input
                    type="number"
                    value={formData.vehicle_year}
                    onChange={(e) => setFormData({ ...formData, vehicle_year: e.target.value })}
                    placeholder="2020"
                  />
                </div>
                <div>
                  <Label>Plate Number *</Label>
                  <Input
                    value={formData.plate_number}
                    onChange={(e) => setFormData({ ...formData, plate_number: e.target.value.toUpperCase() })}
                    placeholder="ABC 1234"
                  />
                </div>
              </div>

              <div>
                <Label>Load Capacity (kg) *</Label>
                <Input
                  type="number"
                  value={formData.load_capacity_kg}
                  onChange={(e) => setFormData({ ...formData, load_capacity_kg: e.target.value })}
                  placeholder="e.g., 1000"
                />
              </div>

              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button 
                  onClick={() => setStep(3)}
                  disabled={!formData.vehicle_make || !formData.plate_number || !formData.load_capacity_kg}
                  className="flex-1 bg-orange-500 hover:bg-orange-600"
                >
                  Continue
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Step 3: Documents */}
        {step === 3 && (
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <FileText className="h-5 w-5 text-orange-500" />
              Required Documents
            </h2>

            <div className="space-y-4">
              <p className="text-sm text-gray-600 bg-orange-50 p-3 rounded-lg">
                Upload clear photos of your documents. All documents will be verified by our team.
              </p>

              {[
                { key: 'drivers_license_url', label: "Driver's License", icon: FileText },
                { key: 'vehicle_registration_url', label: 'Vehicle Registration', icon: Car },
                { key: 'insurance_document_url', label: 'Insurance Document', icon: FileText },
                { key: 'load_capacity_proof_url', label: 'Load Capacity Certificate', icon: FileText }
              ].map((doc) => (
                <div key={doc.key} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <doc.icon className="h-5 w-5 text-gray-400" />
                      <span className="font-medium">{doc.label}</span>
                    </div>
                    <Button variant="outline" size="sm">
                      <Upload className="h-4 w-4 mr-1" />
                      Upload
                    </Button>
                  </div>
                  <Input
                    value={formData[doc.key]}
                    onChange={(e) => setFormData({ ...formData, [doc.key]: e.target.value })}
                    placeholder="Or paste document URL..."
                    className="mt-2 text-sm"
                  />
                </div>
              ))}

              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <Camera className="h-5 w-5 text-gray-400" />
                    <span className="font-medium">Vehicle Photos</span>
                  </div>
                  <Button variant="outline" size="sm">
                    <Camera className="h-4 w-4 mr-1" />
                    Add Photos
                  </Button>
                </div>
                <p className="text-xs text-gray-500">
                  Add photos of your vehicle (front, back, sides, cargo area)
                </p>
              </div>

              <div className="flex gap-2 mt-6">
                <Button 
                  variant="outline"
                  onClick={() => setStep(2)}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button 
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1 bg-orange-500 hover:bg-orange-600"
                  data-testid="submit-application-btn"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Submit Application
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default OperatorApplication;
